import { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import { useSession } from "next-auth/react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { LuX, LuMaximize2, LuMinimize2 } from "react-icons/lu"
import { getAiGlobals } from "services/craft/craft"
import { getSiteByLocale } from "services/sites"
import { useConferenceEvents } from "services/emma/emma"
import ChatMessagesWithTools from "./chatbot/ChatMessagesWithTools"
import ChatInput from "./chatbot/ChatInput"
import AddToCalendarDrawer from "./chatbot/AddToCalendarDrawer"
import AskEmmaButton, { BUTTON_HEIGHT } from "./chatbot/AskEmmaButton"
import useChatbot from "./chatbot/useChatbot"
import useToolSessions from "./chatbot/useToolSessions"
import { BORDER_COLOR, DEFAULT_AGENT_SUBTITLE, HEADER_GRADIENT, HEADER_TITLE, PAGE_BG } from "./chatbot/constants"
import { flattenAgenda } from "./chatbot/utils"

const HIDDEN_PATHS = []
// Wrapper's 24px bottom offset + button + 15px gap.
const MOBILE_PANEL_BOTTOM = 24 + BUTTON_HEIGHT + 15

// Fetches the site's AI config and decides whether the chatbot should exist at
// all. Kept deliberately thin so the expensive chat hooks (useChatbot,
// useConferenceEvents, useToolSessions, ...) live in FloatingChatbotPanel and
// only mount once the CMS gates pass.
export default function FloatingChatbot({ site }) {
    const { locale, pathname } = useRouter()
    const [aiGlobals, setAiGlobals] = useState(null)

    useEffect(() => {
        const craftId = getSiteByLocale(locale)?.craftId
        if (!craftId) return
        getAiGlobals(craftId)
            .then(setAiGlobals)
            .catch(error => console.error("Failed to load AI globals from Craft:", error))
    }, [locale])

    if (!aiGlobals?.showSiteChatbot) return null
    if (!aiGlobals?.siteAgentId) {
        console.warn("FloatingChatbot: siteAgentId is not configured")
        return null
    }
    if (HIDDEN_PATHS.some(p => pathname.startsWith(p))) return null

    return <FloatingChatbotPanel aiGlobals={aiGlobals} site={site} />
}

function FloatingChatbotPanel({ aiGlobals, site }) {
    const [isOpen, setIsOpen] = useState(false)
    // Desktop-only "max" size toggle. When on, the panel grows to 50vw x 90vh.
    const [isMaxed, setIsMaxed] = useState(false)
    const { data: authSession } = useSession()
    // Session being added to a calendar, shown in a drawer that slides up over
    // the chat panel so the user never leaves the chat.
    const [calendarEvent, setCalendarEvent] = useState(null)

    const { messages, input, setInput, isLoading, error, resetKey, handleReset, handleSubmit, onSend } = useChatbot({
        showInlineCards: aiGlobals.showInlineCards,
        agentId: aiGlobals.siteAgentId,
        agendaReady: aiGlobals.agendaReady ?? false,
        programPublished: aiGlobals.programPublished ?? false,
    })

    // Global CMS toggle for the logged-in "saving" features (per-card Save and
    // "Add all sessions to my schedule"). When off, those are suppressed even with
    // user sessions present. Add to Calendar is NOT gated by this — it always shows.
    // Defaults to off when the field is unset.
    const showUserFeatures = aiGlobals.showUserFeatures ?? false

    const emmaMeetingId = site?.meeting?.emmaMeetingId
    const { data: agenda } = useConferenceEvents(
        emmaMeetingId,
        {},
        { queryKey: ["events"], proxy: false },
        !!emmaMeetingId,
    )
    const eventsById = useMemo(() => flattenAgenda(agenda), [agenda])

    const sessionManager = useToolSessions({ messages, resetKey })

    const sessionActions = showUserFeatures
        ? {
              sessionsById: Object.fromEntries(sessionManager.sessions.map(s => [s.id, s])),
              onToggleSave: sessionManager.toggleSave,
              onToggleCal: sessionManager.toggleCal,
              onSaveAll: sessionManager.saveAll,
              isAuthenticated: !!authSession?.user,
          }
        : undefined

    return (
        <Box position="fixed" bottom="24px" right="24px" zIndex={1300}>
            {/* Chat panel */}
            {isOpen && (
                // Below `sm` the 420px panel doesn't fit beside the 24px gutter, so it
                // pins to the viewport and spans edge to edge above the button.
                <Box
                    position={{ base: "fixed", sm: "absolute" }}
                    bottom={{ base: `${MOBILE_PANEL_BOTTOM}px`, sm: `${BUTTON_HEIGHT + 15}px` }}
                    left={{ base: 0, sm: "auto" }}
                    right="0"
                    w={isMaxed ? { base: "100%", sm: "420px", md: "50vw" } : { base: "100%", sm: "420px" }}
                    h={isMaxed ? { base: "540px", md: "90vh" } : "540px"}
                    maxH={{ base: `calc(100dvh - ${MOBILE_PANEL_BOTTOM + 12}px)`, sm: "none" }}
                    borderRadius="16px"
                    overflow="hidden"
                    boxShadow="0 8px 40px rgba(15,25,60,0.25), 0 2px 8px rgba(15,25,60,0.1)"
                    display="flex"
                    flexDirection="column"
                    bg={PAGE_BG}
                    border="1px solid"
                    borderColor={BORDER_COLOR}
                >
                    {/* Header */}
                    <Flex
                        align="center"
                        justify="space-between"
                        px="16px"
                        h="52px"
                        background={HEADER_GRADIENT}
                        flexShrink={0}
                    >
                        <Flex align="center" gap="10px">
                            <Flex
                                align="center"
                                justify="center"
                                w="28px"
                                h="28px"
                                cursor="pointer"
                                onClick={handleReset}
                            >
                                <img
                                    src="/ask-emma-heart.svg"
                                    alt=""
                                    style={{ width: "26px", height: "auto", maxWidth: "none", flexShrink: 0 }}
                                />
                            </Flex>
                            <Flex direction="column" minW={0}>
                                <Text
                                    className="chatbotHeader__title"
                                    fontSize="18px"
                                    color="#fff"
                                    letterSpacing="0.03em"
                                    lineHeight={1}
                                    mt={0}
                                >
                                    {HEADER_TITLE}
                                </Text>
                                <Text
                                    className="chatbotHeader__subtitle"
                                    fontSize="12px"
                                    color="rgba(255,255,255,0.75)"
                                    letterSpacing="0.02em"
                                    mt={0}
                                >
                                    {aiGlobals.agentSubtitle || DEFAULT_AGENT_SUBTITLE}
                                </Text>
                            </Flex>
                        </Flex>
                        <Flex align="center" gap="4px">
                            {/* Size toggle — desktop only */}
                            <Flex
                                display={{ base: "none", md: "flex" }}
                                align="center"
                                justify="center"
                                w="28px"
                                h="28px"
                                cursor="pointer"
                                onClick={() => setIsMaxed(m => !m)}
                                _hover={{ bg: "rgba(255,255,255,0.18)" }}
                                borderRadius="4px"
                                aria-label={isMaxed ? "Shrink chat" : "Expand chat"}
                                title={isMaxed ? "Shrink chat" : "Expand chat"}
                            >
                                {isMaxed ? (
                                    <LuMinimize2 color="rgba(255,255,255,0.85)" size={18} />
                                ) : (
                                    <LuMaximize2 color="rgba(255,255,255,0.85)" size={18} />
                                )}
                            </Flex>
                            <Flex
                                align="center"
                                justify="center"
                                w="28px"
                                h="28px"
                                cursor="pointer"
                                onClick={() => {
                                    setCalendarEvent(null)
                                    setIsOpen(false)
                                }}
                                _hover={{ bg: "rgba(255,255,255,0.18)" }}
                                borderRadius="4px"
                            >
                                <LuX color="rgba(255,255,255,0.85)" size={20} />
                            </Flex>
                        </Flex>
                    </Flex>

                    {/* Messages */}
                    <ChatMessagesWithTools
                        messages={messages}
                        isLoading={isLoading}
                        error={error}
                        onReset={handleReset}
                        startingMessage={aiGlobals.startingMessageSite}
                        suggestedQuestions={aiGlobals.initialMessages?.map(row => row.message) ?? []}
                        onSend={onSend}
                        sessionActions={sessionActions}
                        eventsById={eventsById}
                        onAddToCalendar={setCalendarEvent}
                    />

                    {/* Input */}
                    <ChatInput
                        input={input}
                        onInputChange={e => setInput(e.target.value)}
                        onSubmit={handleSubmit}
                        isLoading={isLoading}
                        agendaReady={aiGlobals.agendaReady ?? false}
                    />

                    {calendarEvent && (
                        <AddToCalendarDrawer event={calendarEvent} onClose={() => setCalendarEvent(null)} />
                    )}
                </Box>
            )}

            <AskEmmaButton onClick={() => setIsOpen(o => !o)} />
        </Box>
    )
}
